<section>
  <div class="section-heading">
    <div>
      <h4>{{ 'software_override.bundle.section_title' | translate }}</h4>
      <p>{{ 'software_override.bundle.description' | translate }}</p>
    </div>
    <span class="selection-count">
      {{
        'software_override.count.selected'
          | translate: { count: selectedReleaseCount }
      }}
    </span>
  </div>

  @if (supportedProjects.length > 0) {
    <div class="supported-projects">
      <strong>{{
        'software_override.bundle.required_projects' | translate
      }}</strong>
      <div>
        @for (project of sortedSupportedProjects; track project.id) {
          <span [matTooltip]="project.id" matTooltipShowDelay="250">
            {{ project.componentName || project.id }}
          </span>
        }
      </div>
    </div>
  }

  @if (unresolvedBundles.length > 0) {
    <div class="unresolved-bundles" role="alert">
      <strong>{{
        'software_override.unresolved.bundles_title' | translate
      }}</strong>
      <p>
        {{ 'software_override.unresolved.bundles_description' | translate }}
      </p>
      <ul>
        @for (bundle of unresolvedBundles; track bundle.id) {
          <li>
            <span>{{ bundle.name || bundle.id }}</span>
            <button
              type="button"
              class="remove-unresolved-bundle"
              [attr.aria-label]="
                'software_override.actions.remove_bundle_override' | translate
              "
              [matTooltip]="
                'software_override.actions.remove_override' | translate
              "
              [disabled]="saving"
              (click)="unresolvedBundleRemoved.emit(bundle)"
            >
              <mat-icon>delete_outline</mat-icon>
            </button>
          </li>
        }
      </ul>
    </div>
  }

  @if (loadError) {
    <div class="message message-error" role="alert">
      <mat-icon>error_outline</mat-icon>
      {{ loadError }}
    </div>
  }

  @if (loading) {
    <div class="empty-state" role="status">
      <span class="loading-indicator" aria-hidden="true"></span>
      {{ 'software_override.bundle.loading_releases' | translate }}
    </div>
  } @else if (releaseOptions.length === 0 && !loadError) {
    <div class="empty-state">
      <mat-icon>deployed_code</mat-icon>
      <div>
        <strong>{{
          'software_override.bundle.no_releases_title' | translate
        }}</strong>
        <span>{{ 'software_override.bundle.no_releases' | translate }}</span>
      </div>
    </div>
  } @else {
    <div class="release-picker">
      <app-autocomplete
        [data]="releaseChoices"
        propertyName="name"
        optionValuePropertyName="id"
        [label]="'software_override.bundle.search_releases' | translate"
        [allowOnlyListValues]="true"
        [multiple]="true"
        [selectedValues]="selectedReleases"
        [disabled]="saving"
        (selectedValuesChange)="updateSelectedReleases($event)"
      ></app-autocomplete>
    </div>

    @if (selectedReleaseOptions.length === 0) {
      <div class="empty-state selected-release-empty">
        {{ 'software_override.bundle.select_releases' | translate }}
      </div>
    } @else {
      <div class="selected-releases-heading">
        <strong>{{
          'software_override.bundle.selected_releases' | translate
        }}</strong>
        <span>{{ selectedReleaseCount }}</span>
      </div>

      <div class="release-grid">
        @for (option of selectedReleaseOptions; track option.release.id) {
          <article class="release-option">
            <div class="release-option-header">
              <label class="release-checkbox">
                <input
                  type="checkbox"
                  [checked]="option.selected"
                  [disabled]="saving"
                  (change)="releaseToggled.emit(option)"
                />
                <span class="release-identity">
                  <strong>{{
                    option.release.name ||
                      ('software_override.bundle.unnamed_release' | translate)
                  }}</strong>
                  @if (option.release.description) {
                    <span>{{ option.release.description }}</span>
                  }
                </span>
              </label>

              @if (option.release.releaseStatus) {
                <span class="release-status">
                  {{ option.release.releaseStatus }}
                </span>
              }
            </div>

            <div class="release-tags">
              @if (option.release.releaseScope) {
                <span>{{ option.release.releaseScope }}</span>
              }
              @if (option.release.releaseHwComponent) {
                <span>{{ option.release.releaseHwComponent }}</span>
              }
            </div>

            <div class="bundle-selection">
              <app-autocomplete
                [data]="option.compatibleBundles"
                propertyName="name"
                optionValuePropertyName="id"
                [label]="'software_override.bundle.bundle_label' | translate"
                [allowOnlyListValues]="true"
                [preSelectedValue]="option.selectedBundle ?? null"
                [disabled]="
                  saving ||
                  option.bundlesLoading ||
                  (option.bundlesLoaded &&
                    option.compatibleBundles.length === 0)
                "
                (optionsRequested)="bundlesRequested.emit(option)"
                (valueSelected)="
                  bundleSelected.emit({ option: option, bundle: $event })
                "
              ></app-autocomplete>

              @if (option.bundlesLoading) {
                <div class="option-state" role="status">
                  <span class="loading-indicator" aria-hidden="true"></span>
                  {{ 'software_override.bundle.loading_bundles' | translate }}
                </div>
              } @else if (option.bundlesLoadError) {
                <div class="option-state option-error" role="alert">
                  <span>{{ option.bundlesLoadError }}</span>
                  <app-button
                    [small]="true"
                    [title]="'software_override.actions.retry' | translate"
                    (click)="bundlesRetry.emit(option)"
                  ></app-button>
                </div>
              } @else if (option.bundlesLoaded && option.bundles.length === 0) {
                <div class="option-state">
                  {{ 'software_override.bundle.no_bundles' | translate }}
                </div>
              } @else if (
                option.bundlesLoaded && option.compatibleBundles.length === 0
              ) {
                <div class="option-state option-error" role="alert">
                  {{
                    'software_override.bundle.no_compatible_bundle' | translate
                  }}
                </div>
              }

              @if (option.selectedBundle; as bundle) {
                <div class="selected-bundle">
                  <div class="selected-bundle-heading">
                    <span>{{
                      'software_override.bundle.selected_bundle' | translate
                    }}</span>
                    <strong>{{ bundle.name || bundle.id }}</strong>
                    <span class="bundle-status">{{
                      bundle.archiveStatus
                    }}</span>
                  </div>

                  @if (getRelevantBundleArtifacts(bundle).length > 0) {
                    <div class="selected-bundle-projects">
                      <span>{{
                        'software_override.bundle.included_projects' | translate
                      }}</span>
                      <ul>
                        @for (
                          artifact of getRelevantBundleArtifacts(bundle);
                          track artifact.id
                        ) {
                          <li>
                            <span>{{
                              artifact.projectName || artifact.projectId
                            }}</span>
                            <strong>{{ artifact.version }}</strong>
                          </li>
                        }
                      </ul>
                    </div>
                  }

                  @if (getOtherBundleArtifacts(bundle); as otherArtifacts) {
                    @if (otherArtifacts.length > 0) {
                      <details class="other-bundle-projects">
                        <summary>
                          <span>{{
                            'software_override.bundle.other_projects'
                              | translate
                          }}</span>
                          <span>{{ otherArtifacts.length }}</span>
                        </summary>
                        <ul>
                          @for (artifact of otherArtifacts; track artifact.id) {
                            <li>
                              <span>{{
                                artifact.projectName || artifact.projectId
                              }}</span>
                              <strong>{{ artifact.version }}</strong>
                            </li>
                          }
                        </ul>
                      </details>
                    }
                  }
                </div>
              }
            </div>
          </article>
        }
      </div>
    }
  }
</section>
